import React, { useCallback, useEffect, useState, useRef } from 'react';
import './phone-call-modal.css';
import { Icon } from '../../icon';
import { Heading } from '../../heading';
import { Paragraph } from '../../paragraph';
import { IconButton } from '../../icon-button';
import {
    END_OF_DAY_SCREEN,
    QUESTIONS_GAME_SCREEN,
    VOTE_GAME_SCREEN,
} from '../../../constants/routes';
import Copy from '../../../copy/phone-call';
import classNames from 'classnames';
import { AudioManager } from '../../../controllers/audioController';
import { browserName } from 'react-device-detect';

const PhoneCallModal = props => {
    const {
        currentCall,
        goToScreen,
        removePhoneCall,
        callExited,
        audioIsOn,
    } = props;

    const phoneCallModalRef = useRef(null);

    useEffect(() => {
        const answerCallTimeout = setTimeout(() => setCallAnswered(true), 3000);
        const showTranscriptTimeout = setTimeout(
            () => setShowTranscript(true),
            4000,
        );

        return () => {
            clearTimeout(answerCallTimeout);
            clearTimeout(showTranscriptTimeout);
        };
    }, []);

    const [callAnswered, setCallAnswered] = useState(false);
    const [showTranscript, setShowTranscript] = useState(false);

    const currentCallInfo = Copy.phone_calls.find(
        phoneCall => phoneCall.phone_callID === currentCall.phone_callID,
    );

    const callAudio = currentCallInfo.mp3Filename.substring(
        0,
        currentCallInfo.mp3Filename.length - 4,
    );
    const [activeAudio, setActiveAudio] = useState(null);
    const [audioPlaying, setAudioPlaying] = useState(false);

    const playTrack = useCallback(audio => {
        AudioManager.play(audio, {
            onEnd: () => {
                setActiveAudio(null);
                setAudioPlaying(false);
            },
        });
        setAudioPlaying(true);
    }, []);

    const route =
        currentCall && currentCall.taskType === 'fail'
            ? END_OF_DAY_SCREEN
            : currentCall && currentCall.taskType === 'question'
            ? QUESTIONS_GAME_SCREEN
            : VOTE_GAME_SCREEN;

    const callIconClasses = classNames({
        'phone-call__phone-icon': true,
        'phone-call__phone-icon--answered': callAnswered,
        'phone-call__phone-icon--hidden': showTranscript,
    });

    const callQuestionClasses = classNames({
        'phone-call__question': true,
        'phone-call__question--hidden': !showTranscript,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const stopAudio = useCallback(() => {
        if (activeAudio !== null && callAnswered === true) {
            AudioManager.getData(activeAudio).source.onended = null;
            AudioManager.stopAll();
            setActiveAudio(null);
            return true;
        }
    });

    useEffect(() => {
        if (callExited === true) {
            if (
                activeAudio !== null &&
                audioIsOn === true &&
                audioPlaying === true
            ) {
                try {
                    stopAudio();
                } catch (err) {
                    setActiveAudio(null);
                    return;
                }
            }
            removePhoneCall();
        }
    }, [
        callExited,
        audioPlaying,
        stopAudio,
        removePhoneCall,
        audioIsOn,
        activeAudio,
    ]);

    const loadAudio = () => {
        AudioManager.load({
            audio: [callAudio],
            onComplete: () => {
                setActiveAudio(callAudio);
            },
        });
    };

    useEffect(() => {
        if (audioIsOn === true) {
            loadAudio();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioIsOn, currentCallInfo]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleScreenClick = useCallback(() => {
        playTrack(activeAudio);
        phoneCallModalRef.current.removeEventListener(
            'click',
            handleScreenClick,
        );
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleScreenTouch = useCallback(() => {
        playTrack(activeAudio);
        phoneCallModalRef.current.removeEventListener(
            'touchend',
            handleScreenTouch,
        );
    });

    useEffect(() => {
        if (
            audioIsOn === true &&
            activeAudio !== null &&
            callAnswered === true &&
            audioPlaying === false
        ) {
            if (browserName === 'Safari') {
                phoneCallModalRef.current.addEventListener(
                    'click',
                    handleScreenClick,
                );
            } else if (browserName === 'Mobile Safari') {
                phoneCallModalRef.current.addEventListener(
                    'touchend',
                    handleScreenTouch,
                );
            } else {
                playTrack(activeAudio);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callAnswered]);

    const handleResponse = response => {
        if (audioPlaying && activeAudio !== null) {
            try {
                stopAudio();
            } catch (err) {
                setActiveAudio(null);
            }
        }
        if (browserName === 'Safari') {
            phoneCallModalRef.current.removeEventListener(
                'click',
                handleScreenClick,
            );
        } else if (browserName === 'Mobile Safari') {
            phoneCallModalRef.current.removeEventListener(
                'touchend',
                handleScreenTouch,
            );
        }
        if (response === 'no') {
            removePhoneCall();
            if (currentCall && currentCall.taskType === 'fail') {
                goToScreen(route);
            }
        } else {
            goToScreen(route);
        }
    };

    return (
        <div className="phone-call-modal" ref={phoneCallModalRef}>
            <div
                className="phone-call__header"
                role="alert"
                aria-label="phone call from party whip"
            >
                <Heading
                    headingType="h3"
                    colorScheme="dark"
                    withSpacing={false}
                >
                    Party Whip
                </Heading>
                <Icon
                    iconName={currentCallInfo.iconName}
                    size="large"
                    altText=""
                />
            </div>
            <div className="phone-call__call-info">
                <div className={callIconClasses}>
                    <Icon iconName="phone" colorScheme="dark" altText="" />
                </div>

                <div className={callQuestionClasses}>
                    <Paragraph
                        colorScheme="dark"
                        textAlign="center"
                        withSpacing={false}
                    >
                        {currentCallInfo.transcript}
                    </Paragraph>
                    <div className="phone-call__decision">
                        <div className="phone-call__decision-choice">
                            <Paragraph colorScheme="dark" withSpacing={false}>
                                No
                            </Paragraph>
                            <IconButton
                                iconName="cross"
                                size="small"
                                onClick={() => handleResponse('no')}
                                altText="no"
                            />
                        </div>
                        <div className="phone-call__decision-choice">
                            <Paragraph colorScheme="dark" withSpacing={false}>
                                Yes
                            </Paragraph>
                            <IconButton
                                iconName="tick"
                                size="small"
                                onClick={() => handleResponse('yes')}
                                altText="yes"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export { PhoneCallModal };
